// Shared by the Client Appendix page and the Word and PDF documents it downloads.
import { AlignmentType, Document, ExternalHyperlink, Packer, Paragraph, TextRun } from 'docx'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'

// title is the page's section heading; docTitle heads the exhibit in the Word document.
// Group by Tier only splits the exhibits marked tierGroupable.
export const CLIENT_APPENDICES = [
    { key: 'appendix_a', tierGroupable: true, title: 'Exhibit A: Approved', docTitle: 'Exhibit A: Acquisition Target List (this list may be modified):' },
    { key: 'appendix_b', title: 'Exhibit B: Excluded', docTitle: 'Exhibit B: Target Exclusion List (this list may be modified):' },
    { key: 'appendix_c', title: 'Exhibit C: Presented, Not Approved', docTitle: 'Exhibit C: Presented to Client but Not Approved (this list may be modified):' },
]

export function formatClientFeedback(feedback) {
    if (!feedback) return ''
    const words = feedback.replaceAll('_', ' ')
    return words.charAt(0).toUpperCase() + words.slice(1)
}

// The columns a user can add after Company Name, in the order they appear. The width is the page
// table's; columns without one share the leftover space with Company Name.
export const CLIENT_APPENDIX_COLUMNS = [
    { label: 'Doing Business As', value: (company) => company.doing_business_as },
    { label: 'City', width: 'w-36', value: (company) => company.city },
    { label: 'State', width: 'w-24', value: (company) => company.state },
    { label: 'Country', width: 'w-28', value: (company) => company.country },
    { label: 'Tier', width: 'w-28', value: (company) => company.tier_object?.tier_name },
    { label: 'Website Url', value: (company) => company.website_url },
    // The feedback that put the company in the appendix, not the feedback for its current campaign
    { label: 'Client Feedback', width: 'w-40', value: (company) => formatClientFeedback(company.client_appendix_feedback) },
]

function websiteHref(url) {
    return /^https?:\/\//i.test(url) ? url : `http://${url}`
}

// One plain line per company: the name, then the chosen columns' values, comma separated. Returned as
// segments ({ text, link }) so the Word and PDF writers can both link the website.
function companySegments(company, columns) {
    const showsWebsite = columns.some((column) => column.label === 'Website Url')
    const parts = [company.company_name, ...columns.map((column) => column.value(company))]
        .filter((part) => part !== null && part !== undefined && String(part).trim() !== '')
    const segments = []
    parts.forEach((part, index) => {
        const text = String(part).trim()
        const separator = index < parts.length - 1 ? ', ' : ''
        if (showsWebsite && text === company.website_url?.trim()) {
            segments.push({ text, link: websiteHref(text) })
            if (separator) segments.push({ text: separator })
        } else {
            segments.push({ text: text + separator })
        }
    })
    return segments
}

// "tier_1" and "tier_(review)_5" read as "Tier 1" and "Tier 5"; companies with no tier come last
export function tierGroups(companies) {
    const groups = new Map()
    for (const company of companies) {
        const name = company.tier_object?.tier_name
        const number = name ? Number(name.match(/(\d+)\s*$/)?.[1]) : NaN
        const key = name ?? ''
        if (!groups.has(key)) {
            groups.set(key, { label: name ? (Number.isNaN(number) ? name : `Tier ${number}`) : 'No Tier', order: Number.isNaN(number) ? Infinity : number, companies: [] })
        }
        groups.get(key).companies.push(company)
    }
    return [...groups.values()].sort((a, b) => a.order - b.order)
}

// The exhibits as format-neutral blocks, each starting on its own page under the client's name:
// title (bold, centered), heading (bold), line (segments), empty ("No companies."), blank
function appendixBlocks(appendix, columnLabels, groupByTier) {
    const columns = CLIENT_APPENDIX_COLUMNS.filter((column) => columnLabels.includes(column.label))
    const clientName = appendix.client.client_name

    const blocks = []
    CLIENT_APPENDICES.forEach(({ key, docTitle, tierGroupable }, index) => {
        const companies = appendix[key]
        blocks.push({ type: 'title', text: clientName, pageBreak: index > 0, tight: true })
        blocks.push({ type: 'title', text: docTitle })
        if (!companies.length) {
            blocks.push({ type: 'empty' })
        } else if (groupByTier && tierGroupable) {
            tierGroups(companies).forEach((group, groupIndex) => {
                if (groupIndex > 0) blocks.push({ type: 'blank' })
                blocks.push({ type: 'heading', text: group.label })
                group.companies.forEach((company) => blocks.push({ type: 'line', segments: companySegments(company, columns) }))
            })
        } else {
            companies.forEach((company) => blocks.push({ type: 'line', segments: companySegments(company, columns) }))
        }
    })
    return blocks
}

// The local day as YYYY-MM-DD, for a published appendix's file names
export function formatFileDate(dateString) {
    const date = new Date(dateString)
    const pad = (number) => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// A published appendix's files carry the day it was published, e.g. "ADC Aerospace Client Appendix 2026-10-26.pdf"
function appendixFileName(appendix, extension, suffix) {
    const name = `${appendix.client.client_name.replace(/[\\/:*?"<>|]/g, '')} Client Appendix`
    return `${suffix ? `${name} ${suffix}` : name}.${extension}`
}

function boldParagraph(text, options = {}) {
    return new Paragraph({ ...options, children: [new TextRun({ text, bold: true })] })
}

function docxParagraph(block) {
    switch (block.type) {
        case 'title':
            return boldParagraph(block.text, { alignment: AlignmentType.CENTER, pageBreakBefore: !!block.pageBreak, ...(block.tight ? { spacing: { after: 0 } } : {}) })
        case 'heading':
            return boldParagraph(block.text)
        case 'empty':
            return new Paragraph({ children: [new TextRun({ text: 'No companies.', italics: true })] })
        case 'blank':
            return new Paragraph({})
        default:
            return new Paragraph({ children: block.segments.map(({ text, link }) => (link
                ? new ExternalHyperlink({ link, children: [new TextRun({ text, style: 'Hyperlink' })] })
                : new TextRun(text))) })
    }
}

export async function downloadClientAppendixDocx(appendix, columnLabels, groupByTier = false, fileNameSuffix = '') {
    // Calibri 11pt with Word's usual paragraph spacing, like the exhibits we've sent before
    const styles = { default: { document: {
        run: { font: 'Calibri', size: 22 },
        paragraph: { spacing: { after: 160, line: 259 } },
    } } }
    const children = appendixBlocks(appendix, columnLabels, groupByTier).map(docxParagraph)
    const blob = await Packer.toBlob(new Document({ styles, sections: [{ children }] }))
    saveAs(blob, appendixFileName(appendix, 'docx', fileNameSuffix))
}

// The same layout as the Word document on a Letter page with 1" margins. jsPDF has no Calibri, so
// it's Helvetica at the same size.
export function downloadClientAppendixPdf(appendix, columnLabels, groupByTier = false, fileNameSuffix = '') {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' })
    const fontSize = 11
    const lineHeight = fontSize * 1.25
    const paragraphAfter = 8
    const margin = 72
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const maxX = pageWidth - margin

    doc.setFontSize(fontSize)
    // y is the baseline of the line being written
    let y = margin + fontSize
    let pageHasText = false

    function newLine() {
        y += lineHeight
        if (y > pageHeight - margin) {
            doc.addPage()
            y = margin + fontSize
        }
    }

    function endParagraph(after = paragraphAfter) {
        newLine()
        y += after
    }

    // Word-wraps segments, keeping each link's words linked and blue like Word's Hyperlink style
    function writeSegments(segments, style = 'normal') {
        doc.setFont('helvetica', style)
        let x = margin
        for (const { text, link } of segments) {
            for (const token of text.split(/(\s+)/)) {
                if (!token) continue
                const width = doc.getTextWidth(token)
                if (/^\s+$/.test(token)) {
                    if (x > margin) x += width
                    continue
                }
                if (x > margin && x + width > maxX) {
                    newLine()
                    x = margin
                }
                if (link) {
                    doc.setTextColor(5, 99, 193)
                    doc.text(token, x, y)
                    doc.setDrawColor(5, 99, 193)
                    doc.setLineWidth(0.5)
                    doc.line(x, y + 1.5, x + width, y + 1.5)
                    doc.link(x, y - fontSize * 0.8, width, fontSize, { url: link })
                    doc.setTextColor(0, 0, 0)
                } else {
                    doc.text(token, x, y)
                }
                x += width
            }
        }
        pageHasText = true
    }

    for (const block of appendixBlocks(appendix, columnLabels, groupByTier)) {
        if (block.type === 'title') {
            if (block.pageBreak && pageHasText) {
                doc.addPage()
                y = margin + fontSize
            }
            doc.setFont('helvetica', 'bold')
            for (const line of doc.splitTextToSize(block.text, maxX - margin)) {
                doc.text(line, pageWidth / 2, y, { align: 'center' })
                newLine()
            }
            y += block.tight ? 0 : paragraphAfter
            pageHasText = true
        } else if (block.type === 'heading') {
            writeSegments([{ text: block.text }], 'bold')
            endParagraph()
        } else if (block.type === 'empty') {
            writeSegments([{ text: 'No companies.' }], 'italic')
            endParagraph()
        } else if (block.type === 'blank') {
            endParagraph()
        } else {
            writeSegments(block.segments)
            endParagraph()
        }
    }

    doc.save(appendixFileName(appendix, 'pdf', fileNameSuffix))
}

// What changed between a published appendix and another (usually the current one), by company:
// added (in the newer one only), removed (in the published one only) and moved (to another exhibit).
// Each change names the exhibit keys it came from and went to.
export function appendixChanges(published, current) {
    const exhibits = (appendix) => {
        const byId = new Map()
        for (const { key } of CLIENT_APPENDICES) {
            for (const company of appendix[key] ?? []) byId.set(company.id, { key, company })
        }
        return byId
    }
    const before = exhibits(published)
    const after = exhibits(current)
    const byName = (a, b) => a.company.company_name.localeCompare(b.company.company_name)

    const added = []
    const moved = []
    for (const [id, now] of after) {
        const then = before.get(id)
        if (!then) added.push({ company: now.company, to: now.key })
        else if (then.key !== now.key) moved.push({ company: now.company, from: then.key, to: now.key })
    }
    const removed = [...before].filter(([id]) => !after.has(id)).map(([, then]) => ({ company: then.company, from: then.key }))
    return { added: added.sort(byName), moved: moved.sort(byName), removed: removed.sort(byName) }
}

// One exhibit's rows for a git-style comparison: the current companies, those new to the exhibit marked
// diff '+', and the published companies no longer in it marked diff '-' with their values then. A company
// that moved exhibits is '-' in one and '+' in the other, with diffNote naming the other exhibit.
export function exhibitDiffRows(published, current, key) {
    const exhibitOf = (appendix) => new Map(
        CLIENT_APPENDICES.flatMap((exhibit) => (appendix[exhibit.key] ?? []).map((company) => [company.id, exhibit.key])))
    const before = exhibitOf(published)
    const after = exhibitOf(current)

    const rows = (current[key] ?? []).map((company) => {
        const was = before.get(company.id)
        if (was === key) return company
        return { ...company, diff: '+', diffNote: was ? `was in ${exhibitName(was)}` : null }
    })
    for (const company of published[key] ?? []) {
        const now = after.get(company.id)
        if (now === key) continue
        rows.push({ ...company, diff: '-', diffNote: now ? `now in ${exhibitName(now)}` : null })
    }
    return rows.sort((a, b) => a.company_name.localeCompare(b.company_name))
}

// "Exhibit A" from "Exhibit A: Approved"
export function exhibitName(key) {
    return CLIENT_APPENDICES.find((appendix) => appendix.key === key)?.title.split(':')[0] ?? key
}
